import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "../../Component";
import http from "../../../utils/http-common";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import { getPublicUrl } from "../../../utils/Utils";
import swal from "sweetalert";

const REASON_LABELS = {
  credentials_missing: "Licence information missing or invalid",
  complaints: "Patient complaints",
  rules_violation: "Platform rules not respected",
  competition: "Competition / conflict of interest",
  other: "Other",
};

function decisionBadge(decision) {
  const d = String(decision || "pending").toLowerCase();
  if (d === "approved") return <span className="badge bg-success">Approved</span>;
  if (d === "rejected") return <span className="badge bg-danger">Rejected</span>;
  return <span className="badge bg-warning text-dark">Pending</span>;
}

/**
 * Read-only listing status. Writes live only on Catalogue go-live
 * (`PractitionerApprovals` / `/teleconsultations/practitioner-approvals`).
 */
export default function PlatformListingPanel({
  clinicId,
  practitionerId,
  title = "Platform listing",
  compact = false,
}) {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const checklistUrl =
    practitionerId && clinicId
      ? `/api/v1/partners/medical/clinics/${clinicId}/practitioners/${practitionerId}/platform-listing/checklist`
      : practitionerId
        ? `/api/v1/admin/practitioner-approvals/${practitionerId}/checklist`
        : `/api/v1/partners/medical/clinics/${clinicId}/platform-listing/checklist`;

  const load = useCallback(async () => {
    if (!clinicId && !practitionerId) return;
    setLoading(true);
    try {
      const res = await http.get(checklistUrl);
      setData(res.data?.data || res.data);
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Could not load listing status"), "error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [checklistUrl, clinicId, practitionerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!clinicId && !practitionerId) return null;
  if (loading) {
    return (
      <div className="p-3 text-center text-soft">
        <div className="spinner-border spinner-border-sm text-primary" role="status" /> Loading listing status…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className={compact ? "border rounded p-3 mb-3" : "card card-bordered mb-3"}>
      <div className={compact ? "" : "card-inner"}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h6 className="mb-0">{title}</h6>
          {decisionBadge(data.decision)}
        </div>

        {data.rejectionReasons?.length > 0 || data.rejectionNote ? (
          <div className="alert alert-light border small mb-3">
            <strong>Last rejection</strong>
            {data.rejectionReasons?.length > 0 ? (
              <ul className="mb-1 ps-3">
                {data.rejectionReasons.map((c) => (
                  <li key={c}>{REASON_LABELS[c] || c}</li>
                ))}
              </ul>
            ) : null}
            {data.rejectionNote ? <div className="text-soft">{data.rejectionNote}</div> : null}
          </div>
        ) : null}

        <div className="d-flex flex-wrap align-items-center gap-2">
          <p className="text-soft small mb-0 flex-grow-1">
            Catalog listing decisions are made in Catalogue go-live.
          </p>
          <Button
            color="primary"
            size="sm"
            outline
            onClick={() => history.push(getPublicUrl("teleconsultations/practitioner-approvals"))}
          >
            Open Catalogue go-live
          </Button>
        </div>
      </div>
    </div>
  );
}
